import { rootApi, rootMiddleware } from '../api';
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { ILogin, ILoginResponse, IRefreshTokenResponse } from '@app/types';
import { RootState } from '../store';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { IUser } from '@tickeat/user';
import { jwtDecode } from 'jwt-decode';

export const ACCESS_TOKEN = 'accessToken';
export const REFRESH_TOKEN = 'refreshToken';

export type AuthenticatedUser = Pick<IUser, '_id' | 'roles' | 'shops' | 'organization' | 'email'>;

export const authApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<ILoginResponse, ILogin>({
      query: (body) => ({
        url: 'auth',
        method: 'POST',
        body,
      }),
    }),
    me: builder.query<AuthenticatedUser, void>({
      queryFn: async (_, api) => {
        const { auth } = api.getState() as RootState;

        if (!auth.accessToken) {
          return { error: { status: 'CUSTOM_ERROR', error: 'Unauthorized' } };
        }

        const user = jwtDecode<IUser>(auth.accessToken);

        return { data: user };
      },
    }),
    forgetPassword: builder.mutation<{ message: string }, { email: string }>({
      query: ({ email }) => ({
        url: 'auth/forgetPassword',
        method: 'POST',
        body: { login: email },
      }),
    }),
    renewPassword: builder.mutation<
      { message: string },
      { token: string; password: string; passwordConfirmation: string }
    >({
      query: ({ token, password, passwordConfirmation }) => ({
        url: `auth/renewPassword?token=${token}`,
        method: 'PUT',
        body: { password, passwordConfirmation },
      }),
    }),
  }),
});

type State = {
  accessToken: string | null;
  refreshToken: string | null;
  user?: AuthenticatedUser | null;
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    accessToken: null,
    refreshToken: null,
    user: null,
  } as State,
  reducers: {
    setInitialState(state, action: PayloadAction<State>) {
      state.accessToken = action.payload?.accessToken ?? null;
      state.refreshToken = action.payload?.refreshToken ?? null;
      state.user = action.payload?.accessToken
        ? jwtDecode<AuthenticatedUser>(action.payload.accessToken)
        : null;
    },
    logout(state) {
      state.accessToken = null;
      state.refreshToken = null;
    },
    setRefreshToken(state, action: PayloadAction<ILoginResponse>) {
      state.refreshToken = action.payload.refreshToken;
    },
    setAccessToken(state, action: PayloadAction<ILoginResponse | IRefreshTokenResponse>) {
      state.accessToken = action.payload.accessToken;
      state.user = action.payload?.accessToken
        ? jwtDecode<AuthenticatedUser>(action.payload.accessToken)
        : null;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
      authSlice.caseReducers.setAccessToken(state, action);
      authSlice.caseReducers.setRefreshToken(state, action);
    });
  },
});

export const selectAccessToken = (state: RootState) => state.auth.accessToken;
export const selectIsAuthenticated = (state: RootState) => !!state.auth.accessToken;

export const selectAuthenticatedUser = (state: RootState) => state.auth.user;

rootMiddleware.startListening({
  matcher: authSlice.actions.logout.match,
  effect: async (_, api) => {
    api.dispatch(rootApi.util.resetApiState());

    await AsyncStorage.removeItem(ACCESS_TOKEN);
    await AsyncStorage.removeItem(REFRESH_TOKEN);
  },
});

rootMiddleware.startListening({
  matcher: isAnyOf(
    authApi.endpoints.login.matchFulfilled,
    authSlice.actions.setInitialState.match,
    authSlice.actions.setAccessToken.match,
    authSlice.actions.setRefreshToken.match,
  ),
  effect: async (action) => {
    const { accessToken, refreshToken } = action.payload;

    if (accessToken) {
      await AsyncStorage.setItem(ACCESS_TOKEN, accessToken);
    }

    if (refreshToken) {
      await AsyncStorage.setItem(REFRESH_TOKEN, refreshToken);
    }
  },
});
